// src/components/Customers/CustomerContent.jsx - Customer Notes & Documents Page
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  FileText,
  StickyNote,
  Upload,
  Trash2,
  Pin,
  PinOff,
  MoreVertical,
  File,
  Image,
  FileSpreadsheet,
  Download,
  ExternalLink,
  Search,
  X,
  Save,
  AlertCircle,
  Tag
} from 'lucide-react';

// Import shadcn components
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Input } from '../ui/input';
import NoteEditor from '../ui/NoteEditor';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

// Import custom components
import PageContainer from '../ui/layout/PageContainer/PageContainer';

// Import services
import FileUploadService from '../../services/files/FileUploadService';

// Import hooks
import { useCustomers } from '../../hooks/useCustomers';
import { useNotes } from '../../hooks/useNotes';
import { useDocuments } from '../../hooks/useDocuments';
import { useTags } from '../../hooks';
import { cn } from '../../utils/cn';

// Import tag components
import { TagPillList } from '../ui/TagPill';
import TagPicker, { TagManager } from '../ui/TagPicker';

const CustomerContent = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();

  // Customer data
  const {
    selectedCustomer,
    selectedCustomerLoading,
    loadCustomerDetails,
    isConnected
  } = useCustomers();

  // Notes data
  const {
    getNotesForCustomer,
    isLoadingNotesForCustomer,
    loadCustomerNotes,
    createNote,
    updateNote,
    deleteNote,
    pinNote,
    selectNote,
    clearSelection,
    startNewNote,
    selectedNote,
    selectedNoteId,
    creatingNote,
    savingNote,
    deletingNote,
    clearNotesForCustomer
  } = useNotes();

  // Documents data
  const {
    getDocumentsForCustomer,
    isLoadingDocumentsForCustomer,
    loadCustomerDocuments,
    addDocument,
    deleteDocument,
    clearDocumentsForCustomer
  } = useDocuments();

  // Tags data
  const {
    addTagToNote,
    removeTagFromNote,
    addTagToDocument,
    removeTagFromDocument
  } = useTags();

  // Local state
  const [noteSearchQuery, setNoteSearchQuery] = useState('');
  const [documentSearchQuery, setDocumentSearchQuery] = useState('');
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [isContentDirty, setIsContentDirty] = useState(false);
  const fileInputRef = useRef(null);
  const noteEditorRef = useRef(null);

  // Local draft title state (canonical: component owns form state)
  const [draftTitle, setDraftTitle] = useState('');
  const titleBaselineRef = useRef('');

  const hasTitleChanges = draftTitle !== titleBaselineRef.current;
  const hasUnsavedChanges = hasTitleChanges || isContentDirty;

  // Initialize title when selected note changes
  useEffect(() => {
    if (selectedNote) {
      if (!hasUnsavedChanges) {
        const title = selectedNote.title || '';
        setDraftTitle(title);
        titleBaselineRef.current = title;
      }
    } else {
      // New note or no selection — reset title
      setDraftTitle('');
      titleBaselineRef.current = '';
    }
  }, [selectedNote?.id, selectedNote?.title]);

  // Canonical: Saving flag watchers
  const prevSavingNote = useRef(false);
  const prevCreatingNote = useRef(false);

  // Save/Create — reset baselines on server confirmation
  useEffect(() => {
    const wasSaving = prevSavingNote.current && !savingNote;
    const wasCreating = prevCreatingNote.current && !creatingNote;

    if (wasSaving || wasCreating) {
      // Reset title baseline to server truth
      const serverTitle = selectedNote?.title || '';
      titleBaselineRef.current = serverTitle;
      setDraftTitle(serverTitle);

      // Reset content baseline to server truth (without wiping editor)
      const serverContent = selectedNote?.content || '';
      noteEditorRef.current?.updateBaseline(serverContent);
    }

    prevSavingNote.current = savingNote;
    prevCreatingNote.current = creatingNote;
  }, [savingNote, creatingNote, selectedNote?.title, selectedNote?.content]);

  // Get data from stores
  const notes = customerId ? getNotesForCustomer(customerId) : [];
  const notesLoading = customerId ? isLoadingNotesForCustomer(customerId) : false;
  const documents = customerId ? getDocumentsForCustomer(customerId) : [];
  const documentsLoading = customerId ? isLoadingDocumentsForCustomer(customerId) : false;

  // Filter notes by search
  const filteredNotes = useMemo(() => {
    if (!noteSearchQuery.trim()) return notes;
    const query = noteSearchQuery.toLowerCase();
    return notes.filter(note =>
      (note.title?.toLowerCase().includes(query)) ||
      (note.content?.toLowerCase().includes(query))
    );
  }, [notes, noteSearchQuery]);

  // Filter documents by search
  const filteredDocuments = useMemo(() => {
    if (!documentSearchQuery.trim()) return documents;
    const query = documentSearchQuery.toLowerCase();
    return documents.filter(doc =>
      (doc.file_name?.toLowerCase().includes(query)) ||
      (doc.description?.toLowerCase().includes(query))
    );
  }, [documents, documentSearchQuery]);

  // Sort notes - pinned first, then by date
  const sortedNotes = useMemo(() => {
    return [...filteredNotes].sort((a, b) => {
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [filteredNotes]);

  // Browser-level protection for unsaved changes (tab close/refresh)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Load customer data on mount
  useEffect(() => {
    if (!isConnected || !customerId) return;

    // Load customer details (minimal, just for header)
    const currentCustomerId = selectedCustomer?.customer?.id;
    if (!currentCustomerId || currentCustomerId !== customerId) {
      loadCustomerDetails(customerId, {
        includeAppointments: false,
        includeInvoices: false,
        includeStatistics: false
      });
    }

    // Load notes and documents
    loadCustomerNotes(customerId);
    loadCustomerDocuments(customerId);
  }, [isConnected, customerId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (customerId) {
        clearNotesForCustomer(customerId);
        clearDocumentsForCustomer(customerId);
      }
      clearSelection();
    };
  }, [customerId]);

  // Extract customer info
  const customer = useMemo(() => {
    if (!selectedCustomer) return null;
    return selectedCustomer.customer || selectedCustomer;
  }, [selectedCustomer]);

  const customerName = customer?.name || customer?.full_name || 'Customer';

  // Helpers
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return File;
    if (mimeType.startsWith('image/')) return Image;
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return FileSpreadsheet;
    if (mimeType.includes('pdf')) return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Handle unsaved changes dialog
  const handleDiscardChanges = () => {
    noteEditorRef.current?.resetContent();
    setIsContentDirty(false);
    setDraftTitle(titleBaselineRef.current);
    setShowUnsavedDialog(false);

    if (pendingAction?.type === 'navigate') {
      navigate(`/customers/${customerId}`);
    } else if (pendingAction?.type === 'selectNote') {
      selectNote(pendingAction.note);
    } else if (pendingAction?.type === 'newNote') {
      startNewNote();
    }
    setPendingAction(null);
  };

  const handleSaveAndContinue = () => {
    const content = noteEditorRef.current?.getHTML() ?? '';
    if (!content?.trim()) return;

    if (selectedNote) {
      const updates = {};
      if (isContentDirty) updates.content = content;
      if (hasTitleChanges) updates.title = draftTitle || null;
      if (Object.keys(updates).length > 0) {
        updateNote(selectedNote.id, updates);
      }
    } else if (customerId) {
      createNote({
        customer_id: customerId,
        content,
        title: draftTitle || null
      });
    }

    // Reset dirty state for the pending action to proceed
    setIsContentDirty(false);
    titleBaselineRef.current = draftTitle;
    setShowUnsavedDialog(false);

    if (pendingAction?.type === 'navigate') {
      setTimeout(() => navigate(`/customers/${customerId}`), 100);
    } else if (pendingAction?.type === 'selectNote') {
      setTimeout(() => selectNote(pendingAction.note), 100);
    } else if (pendingAction?.type === 'newNote') {
      setTimeout(() => startNewNote(), 100);
    }
    setPendingAction(null);
  };

  const handleCancelDialog = () => {
    setShowUnsavedDialog(false);
    setPendingAction(null);
  };

  // Note selection with unsaved changes check
  const handleNoteSelect = (note) => {
    if (hasUnsavedChanges && selectedNoteId !== note.id) {
      setShowUnsavedDialog(true);
      setPendingAction({ type: 'selectNote', note });
    } else {
      selectNote(note);
    }
  };

  const handleNewNote = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
      setPendingAction({ type: 'newNote' });
    } else {
      startNewNote();
    }
  };

  const handleSaveNote = () => {
    const content = noteEditorRef.current?.getHTML() ?? '';
    if (!content?.trim()) return;

    if (selectedNote) {
      // Update existing note — diff-only
      const updates = {};
      if (isContentDirty) updates.content = content;
      if (hasTitleChanges) updates.title = draftTitle || null;
      if (Object.keys(updates).length > 0) {
        updateNote(selectedNote.id, updates);
      }
    } else if (customerId) {
      // Create new note
      createNote({
        customer_id: customerId,
        content,
        title: draftTitle || null
      });
    }
    // Dirty state reset handled by saving flag watchers
  };

  const handleDeleteNote = (noteId, e) => {
    e?.stopPropagation();
    deleteNote(noteId);
    if (selectedNoteId === noteId) {
      clearSelection();
    }
  };

  const handlePinNote = (noteId, isPinned, e) => {
    e?.stopPropagation();
    pinNote(noteId, !isPinned);
  };

  // Document handling
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      for (const file of files) {
        console.log('Uploading file:', file.name, file.size, file.type);

        // Upload file to storage
        const uploadResult = await FileUploadService.uploadDocument(
          file,
          'customer_documents',
          (progress) => setUploadProgress(progress)
        );

        if (uploadResult.success) {
          // Create document record via WebSocket
          addDocument({
            customer_id: customerId,
            file_name: uploadResult.data.file_name,
            file_url: uploadResult.data.file_url,
            file_size: uploadResult.data.file_size,
            mime_type: uploadResult.data.mime_type,
            description: ''
          });
        } else {
          console.error('Upload failed:', uploadResult.error);
          // TODO: Show error toast
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteDocument = (docId, e) => {
    e?.stopPropagation();
    deleteDocument(docId);
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
      setPendingAction({ type: 'navigate' });
    } else {
      navigate(`/customers/${customerId}`);
    }
  };

  // Loading state
  if (!isConnected) {
    return (
      <PageContainer>
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>
            Not connected to server. Please check your connection.
          </AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  if (selectedCustomerLoading && !customer) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer heightMode="viewport" maxWidth="full" padding="none">
      <div className="h-full flex flex-col bg-gradient-to-tl from-[#0a0a0a] from-0% via-[#0b0b0f] via-20% via-[#0c0c18] via-40% via-[#0b0b12] via-60% via-[#0b0b0e] via-80% to-[#0a0a0a] to-100%">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={handleBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to {customerName}
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <h1 className="text-lg font-semibold">Notes & Documents</h1>
            </div>

            {(hasUnsavedChanges || savingNote || creatingNote) && (
              <div className="flex items-center gap-2">
                {hasUnsavedChanges && !savingNote && !creatingNote && (
                  <Badge variant="outline" className="text-amber-400 border-amber-400/30">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Unsaved changes
                  </Badge>
                )}
                <Button size="sm" onClick={handleSaveNote} disabled={savingNote || creatingNote}>
                  <Save className="w-4 h-4 mr-2" />
                  {savingNote || creatingNote ? 'Saving...' : 'Save'}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Main Content - Split View */}
        <div className="flex-1 flex min-h-0">
          {/* Left Sidebar - Notes & Documents Lists */}
          <div className="w-80 flex-shrink-0 border-r border-border flex flex-col">
            {/* Notes Section */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex-shrink-0 p-4 border-b border-border">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold flex items-center gap-2">
                    <StickyNote className="w-4 h-4" />
                    Notes
                    <Badge variant="secondary" className="ml-1 text-xs">
                      {notes.length}
                    </Badge>
                  </h2>
                  <Button size="sm" variant="ghost" onClick={handleNewNote}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search notes..."
                    value={noteSearchQuery}
                    onChange={(e) => setNoteSearchQuery(e.target.value)}
                    className="pl-9 h-8 text-sm"
                  />
                  {noteSearchQuery && (
                    <button
                      onClick={() => setNoteSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      <X className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                    </button>
                  )}
                </div>
              </div>

              <ScrollArea className="flex-1">
                <div className="p-2 space-y-1">
                  {notesLoading ? (
                    <div className="text-center py-8">
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                    </div>
                  ) : sortedNotes.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      {noteSearchQuery ? 'No notes match your search' : 'No notes yet'}
                    </div>
                  ) : (
                    sortedNotes.map((note) => (
                      <div
                        key={note.id}
                        onClick={() => handleNoteSelect(note)}
                        className={cn(
                          "p-3 rounded-lg cursor-pointer group transition-colors",
                          selectedNoteId === note.id
                            ? "bg-primary/10 border border-primary/30"
                            : "hover:bg-muted/50 border border-transparent"
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {note.is_pinned && (
                                <Pin className="w-3 h-3 text-amber-400 flex-shrink-0" />
                              )}
                              <span className="font-medium text-sm truncate">
                                {note.title || 'Untitled Note'}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {note.content?.replace(/<[^>]*>/g, '').slice(0, 100) || 'No content'}
                            </p>
                            <span className="text-xs text-muted-foreground/60 mt-1 block">
                              {formatDateTime(note.created_at)}
                            </span>
                            {/* Tags */}
                            {note.tags && note.tags.length > 0 && (
                              <TagPillList
                                tags={note.tags.map(t => t.tag)}
                                maxVisible={2}
                                size="sm"
                                className="mt-1.5"
                              />
                            )}
                          </div>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => handlePinNote(note.id, note.is_pinned, e)}>
                                {note.is_pinned ? (
                                  <><PinOff className="w-4 h-4 mr-2" /> Unpin</>
                                ) : (
                                  <><Pin className="w-4 h-4 mr-2" /> Pin</>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={(e) => handleDeleteNote(note.id, e)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>

            <Separator />

            {/* Documents Section */}
            <div className="h-64 flex flex-col">
              <div className="flex-shrink-0 p-4 border-b border-border">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Documents
                    <Badge variant="secondary" className="ml-1 text-xs">
                      {documents.length}
                    </Badge>
                  </h2>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                  >
                    <Upload className="w-4 h-4" />
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search documents..."
                    value={documentSearchQuery}
                    onChange={(e) => setDocumentSearchQuery(e.target.value)}
                    className="pl-9 h-8 text-sm"
                  />
                </div>
              </div>

              <ScrollArea className="flex-1">
                <div className="p-2 space-y-1">
                  {documentsLoading ? (
                    <div className="text-center py-4">
                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                    </div>
                  ) : filteredDocuments.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      {documentSearchQuery ? 'No documents match' : 'No documents yet'}
                    </div>
                  ) : (
                    filteredDocuments.map((doc) => {
                      const FileIcon = getFileIcon(doc.mime_type);
                      return (
                        <div
                          key={doc.id}
                          className="p-2 rounded-lg hover:bg-muted/50 group flex items-center gap-2"
                        >
                          <div className="w-8 h-8 rounded bg-muted/50 flex items-center justify-center flex-shrink-0">
                            <FileIcon className="w-4 h-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0 max-w-[140px]">
                            <p className="text-sm font-medium truncate" title={doc.file_name}>{doc.file_name}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {formatFileSize(doc.file_size)} • {formatDate(doc.created_at)}
                            </p>
                            {/* Tags */}
                            {doc.tags && doc.tags.length > 0 && (
                              <TagPillList
                                tags={doc.tags.map(t => t.tag)}
                                maxVisible={2}
                                size="sm"
                                className="mt-1"
                              />
                            )}
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 flex-shrink-0">
                            {/* Tag button */}
                            <TagPicker
                              selectedTags={(doc.tags || []).map(t => t.tag)}
                              onAddTag={(tagName, color) => {
                                addTagToDocument(doc.id, tagName, customerId, color);
                              }}
                              onRemoveTag={(tagId) => {
                                removeTagFromDocument(doc.id, tagId, customerId);
                              }}
                              size="sm"
                            />
                            {doc.file_url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => window.open(doc.file_url, '_blank')}
                              >
                                <ExternalLink className="w-3 h-3" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-destructive"
                              onClick={(e) => handleDeleteDocument(doc.id, e)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>

          {/* Main Editor Area */}
          <div className="flex-1 flex flex-col min-w-0">
            {selectedNoteId !== null ? (
              <>
                {/* Note Title */}
                <div className="flex-shrink-0 px-8 pt-8">
                  <Input
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                    placeholder="Note title (optional)"
                    className="text-2xl font-semibold border-none bg-transparent px-0 h-auto focus-visible:ring-0 placeholder:text-muted-foreground/40"
                  />
                  {selectedNote && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Last updated {formatDateTime(selectedNote.updated_at || selectedNote.created_at)}
                    </p>
                  )}
                  {/* Tags */}
                  <div className="mt-3">
                    <TagManager
                      tags={(selectedNote?.tags || []).map(t => t.tag)}
                      onAddTag={(tagName, color) => {
                        if (selectedNote?.id) {
                          addTagToNote(selectedNote.id, tagName, customerId, color);
                        }
                      }}
                      onRemoveTag={(tagId) => {
                        if (selectedNote?.id) {
                          removeTagFromNote(selectedNote.id, tagId, customerId);
                        }
                      }}
                      size="md"
                    />
                  </div>
                </div>

                {/* Note Content */}
                <div className="flex-1 min-h-0">
                  <NoteEditor
                    ref={noteEditorRef}
                    content={selectedNote?.content || ''}
                    onDirtyChange={setIsContentDirty}
                    isDirty={hasUnsavedChanges}
                    placeholder="Start writing your note..."
                  />
                </div>
              </>
            ) : (
              /* Empty State */
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <StickyNote className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No note selected</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    Select a note from the sidebar or create a new one
                  </p>
                  <Button onClick={handleNewNote}>
                    <Plus className="w-4 h-4 mr-2" />
                    New Note
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <Dialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogDescription>
              You have unsaved changes. Would you like to save them before continuing?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCancelDialog}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDiscardChanges}>
              Discard
            </Button>
            <Button onClick={handleSaveAndContinue}>
              Save & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default CustomerContent;
